import { CONTROL_GROUP_FOCUS_CLASS } from "../lib/controlStyles";
import { Plus, X } from "lucide-react";
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";

import { cn } from "../lib/cn";

/** Same rule as the API: lowercase letters, digits and dashes, starting with a letter or digit. */
export const TAG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
export const TAG_MAX_LENGTH = 48;
export const TAG_MAX_COUNT = 16;

const GOLDEN_ANGLE = 137.508;

/** Turns free text into a valid tag, or `null` when nothing usable is left. */
export function normalizeTag(value: string): string | null {
  const tag = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TAG_MAX_LENGTH)
    .replace(/-+$/, "");
  return TAG_PATTERN.test(tag) ? tag : null;
}

/** Stable hue (0–359) for a tag name. */
export function tagHue(tag: string): number {
  let hash = 0;
  for (const char of tag) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return Math.round((hash * GOLDEN_ANGLE) % 360);
}

/** Style carrying the tag's hue; combine with the `tag-chip` class. */
export function tagStyle(tag: string): CSSProperties {
  return { ["--tag-hue" as string]: tagHue(tag) };
}

export interface TagBadgeProps {
  tag: string;
  onRemove?: () => void;
  className?: string;
}

export function TagBadge({ tag, onRemove, className }: TagBadgeProps) {
  return (
    <span
      style={tagStyle(tag)}
      className={cn(
        "tag-chip inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      <span className="truncate">{tag}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove tag ${tag}`}
          onClick={onRemove}
          className="touch-target -mr-0.5 grid size-4 place-items-center rounded-full hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <X className="size-3" aria-hidden />
        </button>
      ) : null}
    </span>
  );
}

export interface TagListProps {
  tags: readonly string[];
  /** Tags beyond this count collapse into a "+N" chip. */
  max?: number;
  className?: string;
}

export function TagList({ tags, max = 3, className }: TagListProps) {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  const hidden = tags.length - shown.length;
  return (
    <span className={cn("flex flex-wrap items-center gap-1", className)}>
      {shown.map((tag) => (
        <TagBadge key={tag} tag={tag} />
      ))}
      {hidden > 0 ? (
        <span className="text-xs text-muted" title={tags.slice(max).join(", ")}>
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}

export interface TagInputProps {
  label: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  /** Existing tags offered while typing. */
  suggestions?: readonly string[];
  /**
   * Tags used recently elsewhere, most recent first, offered as one-click chips
   * on a single line under the field: as many as fit, never a second line.
   */
  recent?: readonly string[];
  placeholder?: string;
  className?: string;
}

/**
 * One line of quiet chips that add a recent tag. Chips that would wrap stay
 * rendered (to measure) but hidden and out of the tab order.
 */
function RecentTags({
  tags,
  onAdd,
  label,
}: {
  tags: readonly string[];
  onAdd: (tag: string) => void;
  label: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(tags.length);
  const key = tags.join(",");
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const chips = [...row.querySelectorAll<HTMLElement>("[data-recent-tag]")];
      const top = chips[0]?.offsetTop;
      const count = chips.findIndex((chip) => chip.offsetTop !== top);
      setFit(count === -1 ? chips.length : count);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [key]);
  if (tags.length === 0) return null;
  return (
    <div
      ref={rowRef}
      role="group"
      aria-label={label}
      className="mt-1.5 flex h-6 flex-wrap items-center gap-1.5 overflow-hidden"
    >
      <span className="text-xs text-muted">Recent</span>
      {tags.map((tag, index) => {
        const hidden = index >= fit;
        return (
          <button
            key={tag}
            type="button"
            data-recent-tag
            aria-hidden={hidden || undefined}
            tabIndex={hidden ? -1 : undefined}
            title={`Add tag ${tag}`}
            onClick={() => onAdd(tag)}
            className={cn(
              "inline-flex h-6 max-w-40 items-center gap-0.5 rounded-full border border-dashed border-border px-2 text-xs text-secondary transition-colors",
              "hover:border-solid hover:border-control-border-hover hover:text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
              hidden && "invisible",
            )}
          >
            <Plus className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{tag}</span>
          </button>
        );
      })}
    </div>
  );
}

export function TagInput({
  label,
  value,
  onChange,
  suggestions = [],
  recent = [],
  placeholder = "Add a tag",
  className,
}: TagInputProps) {
  const [draft, setDraft] = useState("");
  const listId = useId();
  const full = value.length >= TAG_MAX_COUNT;

  function commit(raw: string) {
    const tag = normalizeTag(raw);
    setDraft("");
    if (!tag || value.includes(tag) || full) return;
    onChange([...value, tag]);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft);
    } else if (event.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  const offered = full ? [] : recent.filter((tag) => !value.includes(tag));

  return (
    <div className="min-w-0">
      <div
        className={cn(
          "touch-target flex min-h-10 flex-wrap items-center gap-1.5 rounded-control border border-control-border bg-surface px-2 py-1.5 transition-colors",
          "hover:border-control-border-hover",
          CONTROL_GROUP_FOCUS_CLASS,
          className,
        )}
      >
        {value.map((tag) => (
          <TagBadge
            key={tag}
            tag={tag}
            onRemove={() => onChange(value.filter((item) => item !== tag))}
          />
        ))}
        <input
          aria-label={label}
          list={listId}
          value={draft}
          disabled={full}
          maxLength={TAG_MAX_LENGTH}
          placeholder={full ? "Tag limit reached" : placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => draft && commit(draft)}
          className="min-w-24 flex-1 bg-transparent px-1 text-sm text-primary outline-none placeholder:text-muted disabled:cursor-not-allowed"
        />
        <datalist id={listId}>
          {[...new Set([...recent, ...suggestions])]
            .filter((tag) => !value.includes(tag))
            .map((tag) => (
              <option key={tag} value={tag} />
            ))}
        </datalist>
      </div>
      <RecentTags tags={offered} label={`Recent ${label.toLowerCase()}`} onAdd={commit} />
    </div>
  );
}

export interface TagFilterProps {
  tags: readonly { tag: string; count: number }[];
  /** Selected tag, or `null` for the whole list. */
  selected: string | null;
  onSelect: (tag: string | null) => void;
  total: number;
  label?: string;
  allLabel?: string;
  className?: string;
}

/** A row of tag chips that narrows a list to one tag, with an "All" chip to come back. */
export function TagFilter({
  tags,
  selected,
  onSelect,
  total,
  label = "Filter by tag",
  allLabel = "All",
  className,
}: TagFilterProps) {
  if (tags.length === 0) return null;
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mb-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex flex-wrap items-center gap-1.5", className)}
    >
      <button
        type="button"
        aria-pressed={selected === null}
        onClick={() => onSelect(null)}
        className={cn(
          base,
          "border border-border bg-surface text-secondary hover:bg-surface-elevated",
          "aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-surface",
        )}
      >
        {allLabel}
        <span className="tabular-nums opacity-70">{total}</span>
      </button>
      {tags.map(({ tag, count }) => (
        <button
          key={tag}
          type="button"
          style={tagStyle(tag)}
          aria-pressed={selected === tag}
          onClick={() => onSelect(selected === tag ? null : tag)}
          className={cn("tag-chip", base)}
        >
          {tag}
          <span className="tabular-nums opacity-70">{count}</span>
        </button>
      ))}
    </div>
  );
}
